import AlertIcon from '@assets/icons/alert.svg';
import AutoPayIcon from '@assets/icons/autopay.svg';
import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import WalletIcon from '@assets/icons/wallet.svg';
import { AutoPayActionSheet } from './autopay-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import { formatCurrency } from '@/utils/money';
import { formatSummaryDateWithYear } from '@/utils/date';
import { gql } from '@apollo/client';
import { MultiStyleText } from '@/components/multi-style-text';
import { PAYMENT_ROUTES } from '@/navigators/routes';
import { PaymentBannerCarousel } from './payment-banner-carousel';
import {
  PaymentCarouselData,
  PaymentCarouselOptionType,
} from './payment-banner';
import { PaymentPlanActionSheet } from './payment-plan-action-sheet';
import {
  Payments_GetAutoPaySettingQuery,
  Payments_GetAutoPaySettingQueryVariables,
} from '@/types';
import { PaymentsStackParams } from '@/navigators/types';
import { StackNavigationProp } from '@react-navigation/stack';
import { Typography } from '@usepepper/ui/atoms/typography';
import { TYPOGRAPHY } from '@/styles';
import {
  useAuthenticatedUserContext,
  useUserContext,
} from '@/contexts/user-context';
import { useAutopaySettings } from '@/hooks/payments/use-autopay-settings';
import { useGetPaymentBannersData } from '@/hooks/use-get-payment-banners-data';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNavigation } from '@react-navigation/native';
import { usePaymentsContext } from '@/contexts/payments-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';
import { View } from 'react-native';
import { WalletActionSheet } from './wallet-action-sheet';

export const GET_AUTO_PAY_SETTING_QUERY = gql`
  query Payments_GetAutoPaySetting(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    getAutopayStatus(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      auto_payment {
        created_at
        uuid
      }
      show_error_banner
    }
  }
`;

export const PaymentBannerContainer: React.FC = () => {
  const navigation = useNavigation<StackNavigationProp<PaymentsStackParams>>();
  const {
    selectedRestaurantRelationship: { employeeRole: restaurantEmployeeRole },
  } = useAuthenticatedUserContext();
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useUserContext();
  const { isSmallLayout } = useLayoutContext();

  const [styles, theme] = useStyles();

  const { autoPaySelfServeEnabled, currencyCode, selectedPaymentMethod } =
    usePaymentsContext();
  const { hasSavedAutoPaySetting } = useAutopaySettings();
  const [showPaymentPlanActionSheet, setShowPaymentPlanActionSheet] =
    useState(false);
  const [showAutoPayModal, setShowAutoPayModal] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const { data: apStatusData, loading: apStatusLoading } = useWrappedQuery<
    Payments_GetAutoPaySettingQuery,
    Payments_GetAutoPaySettingQueryVariables
  >(GET_AUTO_PAY_SETTING_QUERY, {
    skip: !supplierUUID || !restaurantUUID,
    variables: {
      restaurantUUID,
      supplierUUID,
    },
  });

  const { data: upcomingPaymentsData, upcomingPP } = useGetPaymentBannersData(
    restaurantUUID,
    supplierUUID,
  );

  const autoPayment = apStatusData?.getAutopayStatus.auto_payment;
  const showErrorBanner =
    autoPayment && apStatusData?.getAutopayStatus.show_error_banner;
  const userCanPayInvoices = !!restaurantEmployeeRole;

  const carouselData: PaymentCarouselData[] = useMemo(() => {
    const autopayErrorCarouselData = [];

    if (!apStatusLoading && showErrorBanner && autoPayment) {
      autopayErrorCarouselData.push({
        content: (
          <Typography variant="paragraph-md">
            {i18n.t('autopay.error.carousel.text', {
              date: formatSummaryDateWithYear(autoPayment.created_at),
            })}
          </Typography>
        ),
        Icon: (
          <View
            style={[
              styles.iconContainer,
              { backgroundColor: theme.sk.color.bg.error },
            ]}
          >
            <AlertIcon
              height={14}
              stroke={theme.sk.color.icon.negative.primary}
              width={14}
            />
          </View>
        ),
        onPress: () => {
          if (isSmallLayout) {
            navigation.push(PAYMENT_ROUTES.PaymentMethod, {
              requireSavedPaymentMethod: true,
            });
          } else {
            setShowWalletModal(true);
          }
        },
        type: PaymentCarouselOptionType.AutopayError,
      });
    }

    const enrollmentCarouselData = [];

    if (
      autoPaySelfServeEnabled &&
      userCanPayInvoices &&
      !hasSavedAutoPaySetting
    ) {
      enrollmentCarouselData.push({
        content: (
          <Typography variant="paragraph-md">
            {i18n.t('autopay.enrollment.carousel.text')}
          </Typography>
        ),
        Icon: (
          <View
            style={[
              styles.iconContainer,
              { backgroundColor: theme.sk.color.bg.success },
            ]}
          >
            <AutoPayIcon
              height={14}
              stroke={theme.sk.color.icon.primary}
              width={14}
            />
          </View>
        ),
        onPress: () => {
          if (isSmallLayout) {
            navigation.push(PAYMENT_ROUTES.AutoPay);
          } else {
            setShowAutoPayModal(true);
          }
        },
        type: PaymentCarouselOptionType.AutopayEnrollment,
      });
    }

    if (!selectedPaymentMethod) {
      enrollmentCarouselData.push({
        content: (
          <Typography variant="paragraph-md">
            {i18n.t('autopay.no_payment_method.carousel.text')}
          </Typography>
        ),
        Icon: (
          <View
            style={[
              styles.iconContainer,
              { backgroundColor: theme.sk.color.bg.warning },
            ]}
          >
            <WalletIcon
              height={14}
              stroke={theme.sk.color.icon.warning.primary}
              width={14}
            />
          </View>
        ),
        onPress: () => {
          if (isSmallLayout) {
            navigation.push(PAYMENT_ROUTES.PaymentMethod, {
              requireSavedPaymentMethod: true,
            });
          } else {
            setShowWalletModal(true);
          }
        },
        type: PaymentCarouselOptionType.NoPaymentMethod,
      });
    }

    const upcomingPaymentCarouselData = (upcomingPaymentsData || []).map(
      (upcomingPaymentData) => ({
        content: (
          <MultiStyleText
            defaultStyle={[TYPOGRAPHY.p, styles.ellipsizedText]}
            emphasisStyle={styles.dateText}
            forceSingleLine
          >
            {upcomingPaymentData.text}
          </MultiStyleText>
        ),
        onPress:
          upcomingPaymentData.type === PaymentCarouselOptionType.NextPaymentPlan
            ? () => setShowPaymentPlanActionSheet(true)
            : undefined,
        rightText: formatCurrency(upcomingPaymentData.amount, currencyCode),
        type: upcomingPaymentData.type,
      }),
    );

    return [
      ...autopayErrorCarouselData,
      ...enrollmentCarouselData,
      ...upcomingPaymentCarouselData,
    ];
  }, [
    apStatusLoading,
    showErrorBanner,
    autoPayment,
    autoPaySelfServeEnabled,
    userCanPayInvoices,
    hasSavedAutoPaySetting,
    selectedPaymentMethod,
    upcomingPaymentsData,
    styles,
    theme,
    isSmallLayout,
    navigation,
    currencyCode,
  ]);

  return (
    <View style={styles.base}>
      {carouselData.length > 0 ? (
        <>
          <PaymentBannerCarousel data={carouselData} />
          <PaymentPlanActionSheet
            endDate={upcomingPP?.estimated_end_date!}
            installmentAmount={formatCurrency(
              upcomingPP?.payment_plan?.installment_amount_in_micros!,
              currencyCode,
            )}
            installmentPeriod={
              upcomingPP?.payment_plan?.task_frequency_settings?.frequency[
                'type'
              ]
            }
            isVisible={showPaymentPlanActionSheet}
            onClose={() => setShowPaymentPlanActionSheet(false)}
            startDate={upcomingPP?.payment_plan?.start_date!}
            supplierName={upcomingPP?.payment_plan?.supplier?.name!}
            totalAmount={formatCurrency(
              upcomingPP?.payment_plan?.total_amount_in_micros!,
              currencyCode,
            )}
          />
        </>
      ) : null}
      {showAutoPayModal ? (
        <AutoPayActionSheet onClose={() => setShowAutoPayModal(false)} />
      ) : null}
      {showWalletModal ? (
        <WalletActionSheet
          onClose={() => {
            setShowWalletModal(false);
          }}
        />
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {},
  dateText: {
    color: theme.colors.darkGreen,
  },
  ellipsizedText: {
    color: theme.sk.color.text.secondary,
    flex: 1,
    fontSize: theme.fontSizes.small,
  },
  iconContainer: {
    alignItems: 'center',
    borderRadius: 24,
    height: 24,
    justifyContent: 'center',
    marginRight: 8,
    width: 24,
  },
}));
